import React, { useState, useEffect } from 'react';
import { Input } from '@appkit4/react-components/field';
import { Select } from '@appkit4/react-components/select';
import { Button } from '@appkit4/react-components/button';
import Icon from '../../components/reusables/Icon';
import useToast from '../../helpers/useToast';

const PROJECT_DATA_RULE_TYPE = '6';

type SplitKeywordRow = {
    id: number;
    keywords: string;
    targetField: string;
};

type SplitPoolAssignment = {
    id: number;
    poolCode: string;
    percentage: string;
};

export type SplitRuleFormValues = {
    splitRuleName: string;
    description: string;
    priority: string;
    ruleType: string;
    projectId: string;
    keywordRows: SplitKeywordRow[];
    poolAssignments: SplitPoolAssignment[];
};

type SplitRuleFormProps = {
    onSubmit: (values: SplitRuleFormValues) => void;
    initialValues?: SplitRuleFormValues;
    targetFieldOptions?: { value: string; label: string }[];
    poolCodeOptions?: { value: string; label: string }[];
    ruleTypeOptions?: { value: string; label: string }[];
    projectOptions?: { value: string; label: string }[];
    onRuleTypeChange?: (ruleType: string) => void;
    submitLabel?: string;
};

const SplitRuleForm: React.FC<SplitRuleFormProps> = ({
    onSubmit,
    initialValues,
    targetFieldOptions = [],
    poolCodeOptions = [],
    ruleTypeOptions = [
        { value: '7', label: 'Fixed Asset Data' },
        { value: '6', label: 'Project Data' },
    ],
    projectOptions = [],
    onRuleTypeChange,
    submitLabel,
}) => {
    const [splitRuleName, setSplitRuleName] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('');
    const [ruleType, setRuleType] = useState('');

    // '0' is All Projects, matching the ProjectID sentinel the backend stores.
    const [projectId, setProjectId] = useState('0');

    const [keywordRows, setKeywordRows] = useState<SplitKeywordRow[]>([]);

    const [poolAssignments, setPoolAssignments] = useState<
        SplitPoolAssignment[]
    >([]);

    useEffect(() => {
        if (initialValues) return;
        setKeywordRows(
            targetFieldOptions.map((opt, i) => ({
                id: i + 1,
                keywords: '',
                targetField: opt.value,
            }))
        );
    }, [targetFieldOptions, initialValues]);

    useEffect(() => {
        if (initialValues) return;
        setPoolAssignments(
            poolCodeOptions.map((opt, i) => ({
                id: i + 1,
                poolCode: opt.value,
                percentage: '',
            }))
        );
    }, [poolCodeOptions, initialValues]);

    useEffect(() => {
        if (!initialValues) return;

        setSplitRuleName(initialValues.splitRuleName ?? '');
        setDescription(initialValues.description ?? '');
        setPriority(initialValues.priority ?? '');
        setRuleType(initialValues.ruleType ?? '');
        setProjectId(initialValues.projectId ?? '0');
        setKeywordRows(
            initialValues.keywordRows && initialValues.keywordRows.length > 0
                ? initialValues.keywordRows
                : targetFieldOptions.map((opt, i) => ({
                      id: i + 1,
                      keywords: '',
                      targetField: opt.value,
                  }))
        );
        setPoolAssignments(
            initialValues.poolAssignments &&
                initialValues.poolAssignments.length > 0
                ? initialValues.poolAssignments
                : poolCodeOptions.map((opt, i) => ({
                      id: i + 1,
                      poolCode: opt.value,
                      percentage: '',
                  }))
        );
    }, [initialValues, targetFieldOptions, poolCodeOptions]);

    const handleKeywordChange = (id: number, keywords: string) => {
        setKeywordRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, keywords } : row))
        );
    };

    const handleResetKeywordRow = (id: number) => {
        setKeywordRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, keywords: '' } : row))
        );
    };

    const handlePoolPercentageChange = (id: number, percentage: string) => {
        setPoolAssignments((prev) =>
            prev.map((p) => (p.id === id ? { ...p, percentage } : p))
        );
    };

    const showToast = useToast();

    const handleSubmit = () => {
        const missing: string[] = [];
        if (!splitRuleName.trim())
            missing.push('Please enter a split rule name');
        if (!description.trim()) missing.push('Please enter a description');
        if (!priority.trim()) missing.push('Please enter a priority');
        else if (!Number.isFinite(Number(priority)))
            missing.push('Priority must be a number');
        if (!ruleType) missing.push('Please select a rule type');
        if (!keywordRows.some((r) => r.keywords.trim()))
            missing.push('At least one keyword condition is required');
        const filledPools = poolAssignments.filter((p) => p.percentage);
        if (filledPools.length === 0)
            missing.push('At least one pool allocation is required');
        else if (
            filledPools.some((p) => !Number.isFinite(Number(p.percentage)))
        )
            missing.push('Pool allocation percentages must be numbers');
        else {
            // Number, not parseFloat: "12abc" must fail rather than silently become 12.
            const total = filledPools.reduce(
                (sum, p) => sum + Number(p.percentage),
                0
            );
            if (Math.abs(total - 100) > 0.01)
                missing.push(
                    `Pool allocations must total 100% (currently ${total}%)`
                );
        }
        if (missing.length > 0) {
            showToast(
                'One or more validation errors occurred.',
                missing.join('\n'),
                'error'
            );
            return;
        }

        onSubmit({
            splitRuleName,
            description,
            priority,
            ruleType,
            projectId: ruleType === PROJECT_DATA_RULE_TYPE ? projectId : '0',
            keywordRows,
            poolAssignments,
        });
    };

    return (
        <div className="split-rule-form" data-testid="split-rule-form">
            <div className="split-rule-row split-rule-row--two-cols">
                <div className="split-rule-field">
                    <Input
                        title="Split rule Name"
                        data-testid="rule-name-input"
                        value={splitRuleName}
                        onChange={(value: string) => setSplitRuleName(value)}
                        required
                    />
                </div>
                <div className="split-rule-field">
                    <Input
                        title="Description"
                        data-testid="rule-description-input"
                        value={description}
                        onChange={(value: string) => setDescription(value)}
                        required
                    />
                </div>
            </div>

            <div className="split-rule-row split-rule-row--two-cols">
                <div className="split-rule-field">
                    <Input
                        title="Split rule priority"
                        data-testid="rule-priority-input"
                        value={priority}
                        onChange={(value: string) => setPriority(value)}
                        required
                    />
                </div>
                <div className="split-rule-field">
                    <Select
                        data={ruleTypeOptions}
                        value={ruleType}
                        placeholder="Rule Type"
                        onSelect={(val: any) => {
                            const next =
                                typeof val === 'string'
                                    ? val
                                    : (val?.value ?? '');
                            setRuleType(next);
                            onRuleTypeChange?.(next);
                        }}
                    />
                </div>
                {ruleType === PROJECT_DATA_RULE_TYPE ? (
                    <div className="split-rule-field">
                        <Select
                            data={[
                                { value: '0', label: 'All Projects' },
                                ...projectOptions,
                            ]}
                            value={projectId}
                            placeholder="Project Scope"
                            data-testid="rule-project-scope-select"
                            onSelect={(val: any) =>
                                setProjectId(
                                    typeof val === 'string'
                                        ? val
                                        : (val?.value ?? '0')
                                )
                            }
                        />
                    </div>
                ) : null}
            </div>

            <div className="split-rule-row">
                <span className="split-rule-label">
                    Rule search keywords <span className="required">*</span>
                </span>
            </div>

            {keywordRows.map((row, index) => {
                const targetLabel =
                    targetFieldOptions.find((o) => o.value === row.targetField)
                        ?.label ?? '';

                return (
                    <div
                        className="split-rule-row split-rule-row--keywords"
                        key={row.id}
                    >
                        <div className="split-rule-field split-rule-field--keywords">
                            <Input
                                title="Keywords"
                                value={row.keywords}
                                onChange={(value: string) =>
                                    handleKeywordChange(row.id, value)
                                }
                                required
                            />
                        </div>
                        <div className="split-rule-field split-rule-field--target">
                            <span>{targetLabel}</span>
                        </div>

                        <div className="split-rule-bin">
                            <Icon
                                iconName="Appkit4-icon icon-delete-outline"
                                onClick={() => handleResetKeywordRow(row.id)}
                            />
                        </div>
                    </div>
                );
            })}

            <div className="split-rule-row">
                <span className="split-rule-label">
                    Assigns to pools in these proportions{' '}
                    <span className="required">*</span>
                </span>
            </div>

            <div className="split-rule-row--pools-wrapper">
                {poolAssignments.map((p) => {
                    const poolLabel =
                        poolCodeOptions.find((o) => o.value === p.poolCode)
                            ?.label ?? p.poolCode;

                    return (
                        <div
                            className="split-rule-row split-rule-row--pools"
                            key={p.id}
                        >
                            <div className="split-rule-bin">
                                <Icon iconName="Appkit4-icon icon-archive-outline" />
                            </div>
                            <div className="split-rule-field split-rule-field--pool-label">
                                <span>{poolLabel}</span>
                            </div>
                            <div className="split-rule-field split-rule-field--percent">
                                <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={p.percentage}
                                    onChange={(value: string) =>
                                        handlePoolPercentageChange(p.id, value)
                                    }
                                    placeholder="%"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="split-rule-footer">
                <Button
                    kind="primary"
                    onClick={handleSubmit}
                    data-testid="rule-submit-btn"
                >
                    {submitLabel ??
                        (initialValues ? 'Save Changes' : 'Create Rule')}
                </Button>
            </div>
        </div>
    );
};

export default SplitRuleForm;
